import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Countdown timer for rate-limiting a "resend" action.
 *
 * `start()` kicks off a countdown from `seconds` to 0, ticking once per second.
 * While `secondsLeft > 0` the caller should keep its resend control disabled.
 * Restarting mid-countdown resets the clock rather than stacking intervals.
 */
export function useResendCooldown(seconds = 30) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clear();
    setSecondsLeft(seconds);
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clear();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [seconds, clear]);

  const reset = useCallback(() => {
    clear();
    setSecondsLeft(0);
  }, [clear]);

  // Stop the timer if the component unmounts mid-countdown.
  useEffect(() => clear, [clear]);

  return { secondsLeft, isCoolingDown: secondsLeft > 0, start, reset };
}
